import { useState, useEffect, useRef } from "react";
import { db } from "@/lib/firebase";
import { collection, getDocs, query, orderBy, deleteDoc, doc } from "firebase/firestore";
import { toast } from "sonner";
import { printDocument } from "@/lib/printHelper";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Search, Eye, Printer, FileText, Receipt, Wrench, Archive,
  Loader2, Trash2, AlertTriangle,
} from "lucide-react";
import InvoicePreview from "@/components/preview/InvoicePreview";
import QuotationPreview from "@/components/preview/QuotationPreview";
import ServiceBillPreview from "@/components/preview/ServiceBillPreview";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return dateStr; }
};

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(val || 0);

// ─── Delete confirmation flow ─────────────────────────────────────────────────
// Step 1: AlertDialog "Are you sure?"
// Step 2: Modal — type "DELETE" to permanently confirm

const DeleteConfirmFlow = ({ target, onConfirmed, onCancel }) => {
  const [step, setStep] = useState(1); // 1 = first confirm, 2 = type DELETE
  const [typed, setTyped] = useState("");
  const [deleting, setDeleting] = useState(false);

  const handleFirstConfirm = () => setStep(2);

  const handleFinalDelete = async () => {
    if (typed !== "DELETE") return;
    setDeleting(true);
    try {
      await onConfirmed();
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    setStep(1);
    setTyped("");
    onCancel();
  };

  if (!target) return null;

  const numberField = target.collectionName === "invoices"
    ? target.doc.invoiceNumber
    : target.collectionName === "quotations"
    ? target.doc.quotationNumber
    : target.doc.billNumber;

  return (
    <>
      {/* Step 1 — First confirmation */}
      <AlertDialog open={step === 1}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2 rounded-full bg-destructive/10">
                <Trash2 className="h-5 w-5 text-destructive" />
              </div>
              <AlertDialogTitle className="font-heading">Delete Record?</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-sm space-y-1">
              <p>You are about to delete:</p>
              <p className="font-semibold text-foreground">{numberField || "Untitled"} — {target.doc.customerName || "Unknown Customer"}</p>
              <p className="text-destructive font-medium mt-2">⚠️ This will permanently remove the record from Firestore.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleFirstConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Yes, Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Step 2 — Type DELETE to confirm permanently */}
      <Dialog open={step === 2} onOpenChange={(o) => { if (!o) handleCancel(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2 rounded-full bg-destructive/10">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <DialogTitle className="font-heading text-destructive">Permanent Deletion</DialogTitle>
            </div>
            <DialogDescription className="space-y-2 text-sm">
              <p>This action <strong>cannot be undone</strong>. The record will be permanently deleted from the database.</p>
              <p className="font-medium text-foreground mt-3">
                Type <span className="font-bold text-destructive bg-destructive/10 px-1.5 py-0.5 rounded">DELETE</span> to confirm:
              </p>
            </DialogDescription>
          </DialogHeader>

          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="Type DELETE here"
            className="border-destructive/40 focus-visible:ring-destructive/40"
            autoFocus
          />

          <DialogFooter className="gap-2 mt-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleFinalDelete}
              disabled={typed !== "DELETE" || deleting}
              className="gap-2"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {deleting ? "Deleting..." : "Delete Permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ─── Table Row ────────────────────────────────────────────────────────────────
const RecordRow = ({ doc, type, onView, onPrint, onDelete }) => {
  const numberField =
    type === "invoices" ? "invoiceNumber" :
    type === "quotations" ? "quotationNumber" : "billNumber";
  const dateField =
    type === "invoices" ? "invoiceDate" :
    type === "quotations" ? "quotationDate" : "billDate";

  return (
    <tr className="hover:bg-secondary/30 transition-colors">
      <td className="px-4 py-3 text-sm font-medium text-foreground">{doc[numberField] || "—"}</td>
      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{formatDate(doc[dateField])}</td>
      <td className="px-4 py-3 text-sm text-foreground">{doc.customerName || "—"}</td>
      <td className="px-4 py-3 text-sm font-medium text-primary whitespace-nowrap">{formatCurrency(doc.total)}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" onClick={() => onView(doc)} className="gap-1.5 h-8 text-xs">
            <Eye className="h-3.5 w-3.5" /> View
          </Button>
          <Button variant="secondary" size="sm" onClick={() => onPrint(doc)} className="gap-1.5 h-8 text-xs">
            <Printer className="h-3.5 w-3.5" /> Print
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(doc)}
            className="gap-1.5 h-8 text-xs text-destructive border-destructive/30 hover:bg-destructive/10 hover:border-destructive/60"
          >
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </Button>
        </div>
      </td>
    </tr>
  );
};

// ─── Empty State ──────────────────────────────────────────────────────────────
const EmptyState = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <Archive className="h-12 w-12 text-muted-foreground/40 mb-4" />
    <p className="text-muted-foreground font-medium">{message}</p>
    <p className="text-sm text-muted-foreground/60 mt-1">Records will appear here once saved</p>
  </div>
);

// ─── Table ────────────────────────────────────────────────────────────────────
const RecordsTable = ({ records, type, search, onView, onPrint, onDelete }) => {
  const filtered = records.filter((r) => {
    const term = search.toLowerCase();
    const nf =
      type === "invoices" ? "invoiceNumber" :
      type === "quotations" ? "quotationNumber" : "billNumber";
    return (
      (r.customerName || "").toLowerCase().includes(term) ||
      (r[nf] || "").toLowerCase().includes(term)
    );
  });

  const headers =
    type === "invoices"
      ? ["Invoice No.", "Date", "Customer Name", "Amount", "Actions"]
      : type === "quotations"
      ? ["Quotation No.", "Date", "Customer Name", "Amount", "Actions"]
      : ["Bill No.", "Date", "Customer Name", "Amount", "Actions"];

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full">
        <thead>
          <tr className="bg-secondary/50">
            {headers.map((h) => (
              <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {filtered.length === 0 ? (
            <tr><td colSpan={5}>
              <EmptyState message={search ? "No records match your search" : "No records found"} />
            </td></tr>
          ) : filtered.map((d) => (
            <RecordRow key={d.id} doc={d} type={type} onView={onView} onPrint={onPrint} onDelete={onDelete} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const RecordsPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [serviceBills, setServiceBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // View dialog
  const [viewDoc, setViewDoc] = useState(null);
  const [viewType, setViewType] = useState(null);
  const printRef = useRef();

  // Delete flow state
  const [deleteTarget, setDeleteTarget] = useState(null); // { doc, collectionName }

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [invSnap, quotSnap, svcSnap] = await Promise.all([
        getDocs(query(collection(db, "invoices"), orderBy("savedAt", "desc"))),
        getDocs(query(collection(db, "quotations"), orderBy("savedAt", "desc"))),
        getDocs(query(collection(db, "service_bills"), orderBy("savedAt", "desc"))),
      ]);
      setInvoices(invSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setQuotations(quotSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setServiceBills(svcSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch {
      toast.error("Failed to fetch records. Check Firebase configuration.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleView = (d, type) => { setViewDoc(d); setViewType(type); };

  const handlePrint = (d, type) => {
    // 1. Open the view dialog so the document renders in the DOM
    setViewDoc(d);
    setViewType(type);
    // 2. Wait for React to paint the dialog + preview, then print via helper
    setTimeout(() => printDocument(), 700);
  };

  const handleDelete = (d, collectionName) => {
    setDeleteTarget({ doc: d, collectionName });
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    await deleteDoc(doc(db, deleteTarget.collectionName, deleteTarget.doc.id));
    toast.success("Record permanently deleted.");
    // Remove from local state immediately
    if (deleteTarget.collectionName === "invoices")
      setInvoices((prev) => prev.filter((x) => x.id !== deleteTarget.doc.id));
    else if (deleteTarget.collectionName === "quotations")
      setQuotations((prev) => prev.filter((x) => x.id !== deleteTarget.doc.id));
    else
      setServiceBills((prev) => prev.filter((x) => x.id !== deleteTarget.doc.id));
    setDeleteTarget(null);
  };

  const calcSubtotal = (d) =>
    (parseFloat(d.exShowroomPrice) || 0) +
    (parseFloat(d.rtoCharges) || 0) +
    (parseFloat(d.insuranceCharges) || 0) +
    (parseFloat(d.accessoriesCharges) || 0) +
    (parseFloat(d.otherCharges) || 0);

  const calcTax = (d) => (parseFloat(d.cgst) || 0) + (parseFloat(d.sgst) || 0);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className={viewDoc ? "print:hidden space-y-6" : "space-y-6"}>
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-heading text-xl font-semibold text-foreground">View Records</h2>
            <p className="text-sm text-muted-foreground">Browse all saved invoices, quotations, and service bills</p>
          </div>
          <Badge variant="outline" className="text-xs">
            {invoices.length + quotations.length + serviceBills.length} Total Records
          </Badge>
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by customer name or document number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            id="records-search"
          />
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-3 text-muted-foreground">Loading records from Firestore...</span>
          </div>
        ) : (
          <Tabs defaultValue="invoices">
            <TabsList className="grid w-full max-w-lg grid-cols-3">
              <TabsTrigger value="invoices" className="gap-2">
                <Receipt className="h-4 w-4" /> Invoices ({invoices.length})
              </TabsTrigger>
              <TabsTrigger value="quotations" className="gap-2">
                <FileText className="h-4 w-4" /> Quotations ({quotations.length})
              </TabsTrigger>
              <TabsTrigger value="service-bills" className="gap-2">
                <Wrench className="h-4 w-4" /> Service Bills ({serviceBills.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="invoices" className="mt-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-base">Sales Invoices</CardTitle>
                </CardHeader>
                <CardContent>
                  <RecordsTable
                    records={invoices} type="invoices" search={search}
                    onView={(d) => handleView(d, "invoice")}
                    onPrint={(d) => handlePrint(d, "invoice")}
                    onDelete={(d) => handleDelete(d, "invoices")}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="quotations" className="mt-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-base">Vehicle Quotations</CardTitle>
                </CardHeader>
                <CardContent>
                  <RecordsTable
                    records={quotations} type="quotations" search={search}
                    onView={(d) => handleView(d, "quotation")}
                    onPrint={(d) => handlePrint(d, "quotation")}
                    onDelete={(d) => handleDelete(d, "quotations")}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="service-bills" className="mt-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-base">Service Bills</CardTitle>
                </CardHeader>
                <CardContent>
                  <RecordsTable
                    records={serviceBills} type="service_bills" search={search}
                    onView={(d) => handleView(d, "service-bill")}
                    onPrint={(d) => handlePrint(d, "service-bill")}
                    onDelete={(d) => handleDelete(d, "service_bills")}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* View Dialog */}
      <Dialog open={!!viewDoc} onOpenChange={(o) => { if (!o) { setViewDoc(null); setViewType(null); } }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="no-print">
            <DialogTitle className="font-heading">
              {viewType === "invoice" ? "Invoice Preview" :
               viewType === "quotation" ? "Quotation Preview" : "Service Bill Preview"}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2" ref={printRef}>
            {viewDoc && viewType === "invoice" && (
              <InvoicePreview formData={viewDoc} subtotal={calcSubtotal(viewDoc)} tax={calcTax(viewDoc)} total={viewDoc.total || 0} />
            )}
            {viewDoc && viewType === "quotation" && (
              <QuotationPreview formData={viewDoc} total={viewDoc.total || 0} />
            )}
            {viewDoc && viewType === "service-bill" && (
              <ServiceBillPreview
                formData={viewDoc}
                serviceTotal={(viewDoc.serviceItems || []).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0)}
                partsTotal={(viewDoc.partsItems || []).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0)}
                tax={calcTax(viewDoc)}
                total={viewDoc.total || 0}
              />
            )}
          </div>
          <div className="flex justify-end gap-3 mt-4 no-print">
            <Button variant="outline" onClick={() => { setViewDoc(null); setViewType(null); }}>Close</Button>
            <Button onClick={() => printDocument()} className="gap-2 bg-primary text-primary-foreground">
              <Printer className="h-4 w-4" /> Print
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Double-confirm Delete Flow */}
      <DeleteConfirmFlow
        target={deleteTarget}
        onConfirmed={handleDeleteConfirmed}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default RecordsPage;
